import { Injectable, inject } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { Customer } from '@df/session/api/customer';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { NProfiles } from '../../account/pwa/profiles/profile.interfaces';
import { LocalCustomerModel } from '../../customer/customer.model.decorator';
import { NFirstMission } from '../pwa/first-mission/first-mission.component';
import { NAntavo } from './antavo.interface';

/**
 * Service for handling Antavo API interactions
 */
@Injectable({
    providedIn: 'root'
})
export class AntavoService {
    private readonly requestService = inject(RequestService);
    private readonly productResource = inject(ProductResource);
    private customer = inject(Customer);
    private timeoutService = inject(TimeoutService);
    private iCmsBlockResource = inject(ICmsBlockResource);
    private readonly BASE_PATH = 'antavo';
    private challengesResponseCache?: NAntavo.IChallengesResponse;

    /**
     * Fetches challenges from Antavo API
     * @param params Request parameters containing loyaltyId
     * @returns Promise with challenges data
     */
    async getChallenges(loyaltyId: NAntavo.IRequestParams['loyaltyId'], status?: Status): Promise<NAntavo.IChallengesResponse | undefined> {
        try {
            if (!this.challengesResponseCache) {
                this.challengesResponseCache = (await this.requestService.send(status, {
                    path: `${this.BASE_PATH}/challenges`,
                    requestOptions: {
                        method: 'POST',
                        api: 'node'
                    },
                    data: {
                        loyaltyId: loyaltyId
                    }
                })) as NAntavo.IChallengesResponse;
                this.challengesResponseCache.payload.forEach(challenge => {
                    // Extract rosettes points from tags array that matches pattern like '50 Rosettes'
                    const rosettesTag = challenge.tags.find(tag => tag.toLowerCase().includes('rosettes'));
                    if (rosettesTag) {
                        // Extract the number from the tag (e.g., '50 Rosettes' -> 50)
                        const match = rosettesTag.match(/(\d+)\s*rosettes/i);
                        challenge.rosettes = match ? parseInt(match[1], 10) : 0;
                    }
                });
            }

            return this.challengesResponseCache;
        } catch (error) {
            console.error('Error fetching challenges:', error);
            return undefined;
        }
    }

    /**
     * Loads customer data from Antavo
     * @param params Request parameters containing loyaltyId and optional additionalFields
     * @returns Promise with customer data
     */
    async loadCustomer(params: NAntavo.ILoadCustomerParams): Promise<NAntavo.ICustomerResponse> {
        const status = new Status();

        return this.requestService.send(status, {
            path: `${this.BASE_PATH}/customer`,
            requestOptions: {
                method: 'POST',
                api: 'node'
            },
            data: {
                loyaltyId: params.loyaltyId,
                additionalFields: params.additionalFields
            }
        });
    }

    /**
     * Sends push notification through Antavo
     * @param params Request parameters containing loyaltyId and custom data
     * @returns Promise with response data
     */
    async sendPushNotification(params: NAntavo.IPushNotificationParams): Promise<any> {
        const status = new Status();

        return this.requestService.send(status, {
            path: `${this.BASE_PATH}/events`,
            requestOptions: {
                method: 'POST',
                api: 'node'
            },
            data: {
                loyaltyId: params.loyaltyId,
                action: 'push-notification',
                data: params.data
            }
        });
    }

    /**
     * Sends custom event to Antavo
     * @param params Request parameters containing loyaltyId, action and optional data
     * @returns Promise with response data
     */
    async sendCustomEvent(params: NAntavo.ICustomEventParams, status: Status): Promise<any> {
        const result = await this.requestService.send(status, {
            path: `${this.BASE_PATH}/events`,
            requestOptions: {
                method: 'POST',
                api: 'node'
            },
            data: {
                loyaltyId: params.loyaltyId,
                action: params.action,
                data: params.data || {}
            }
        });

        this.clearChallengesCache();
        // Wait 1-2 seconds then refresh member status
        await new Promise(resolve => setTimeout(resolve, 1500));
        await this.refreshMemberStatus();

        return result;
    }

    async finishSimpleMission(mission: NAntavo.IChallenge, fallbackAction: string, status: Status): Promise<any> {
        if (!this.customerModel.loyaltyId) {
            console.warn('No loyalty ID found for customer');
            return;
        }

        const result = await this.sendCustomEvent(
            {
                loyaltyId: this.customerModel.loyaltyId,
                action: mission?.progress.criteria.find(m => m.action)?.action || fallbackAction
            },
            status
        );

        return result;
    }

    /**
     * Fetches rewards from Antavo API
     * @param params Request parameters containing loyaltyId
     * @returns Promise with rewards data
     */
    async getRewards(params: NAntavo.IRequestParams): Promise<NAntavo.IRewardsResponse | undefined> {
        const status = new Status();

        try {
            return (await this.requestService.send(status, {
                path: `${this.BASE_PATH}/rewards`,
                requestOptions: {
                    method: 'POST',
                    api: 'node'
                },
                data: {
                    loyaltyId: params.loyaltyId
                }
            })) as NAntavo.IRewardsResponse;
        } catch (error) {
            console.error('Error fetching rewards:', error);
            return undefined;
        }
    }

    /**
     * Fetches rewards from Antavo API and enhances them with computed flags
     * @param params Request parameters containing loyaltyId
     * @returns Promise with enhanced rewards data
     */
    async getEnhancedRewards(params: NAntavo.IRequestParams): Promise<NAntavo.IEnhancedReward[] | undefined> {
        try {
            const rewardsResponse = await this.getRewards(params);

            if (!rewardsResponse) {
                return undefined;
            }

            if (!Array.isArray(rewardsResponse)) {
                return [];
            }

            if (!rewardsResponse.length) {
                return [];
            }

            // Check if any rewards have product SKUs in description (for future use)
            const potentialSkus = rewardsResponse
                .map(reward => reward.description)
                .filter(desc => desc && /^[A-Z0-9-_]+$/i.test(desc.trim())); // Simple SKU pattern check

            let productMap = new Map<string, Product<IProductDataV2>>();

            // Only attempt to load products if we find potential SKUs
            if (potentialSkus.length > 0) {
                try {
                    const products = await this.productResource.find({
                        filter: { sku: { $in: potentialSkus } },
                        verbosity: 2
                    });

                    products.forEach(product => {
                        if (product.data?.sku) {
                            productMap.set(product.data.sku, product);
                        }
                    });
                } catch (error) {
                    console.error('Failed to load products for rewards:', error);
                }
            }

            // Enhance rewards with computed flags and any matching product data
            const enhancedRewards: NAntavo.IEnhancedReward[] = rewardsResponse.map((reward: NAntavo.IReward) => {
                const product = productMap.get(reward.description);
                const now = new Date();

                // Check if reward is "new" (starts_at is less than 7 days ago)
                let isNew = false;
                if (reward.starts_at) {
                    const startsAt = new Date(reward.starts_at);
                    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    isNew = startsAt > sevenDaysAgo;
                }

                return {
                    ...reward,
                    product,
                    isNew
                };
            });

            return enhancedRewards.filter(r => !!r.product?.data);
        } catch (error) {
            console.error('Failed to load enhanced rewards:', error);
            return undefined;
        }
    }

    async getFirstMission(): Promise<NAntavo.IChallenge | undefined> {
        return this.getCmsBlockMission(NFirstMission.componentId);
    }

    async getProfileMission(): Promise<NAntavo.IChallenge | undefined> {
        return this.getCmsBlockMission(NProfiles.COMPONENT_IDS.accountProfiles);
    }

    private async getCmsBlockMission(componentId: string): Promise<NAntavo.IChallenge | undefined> {
        try {
            // Load CMS block for first mission
            const cmsBlock = await this.iCmsBlockResource.retrieve({ bareUrl: componentId }, undefined);

            if (!cmsBlock) {
                console.warn('No CMS block found for first mission');
                return;
            }
            await this.customerModel.load();

            if (!this.customerModel?.loyaltyId) {
                console.warn('No loyalty ID found for customer');
                return;
            }

            // Get the Antavo mission ID from CMS content
            const antavoMissionId = cmsBlock.data?.content.antavoMissionId;

            if (!antavoMissionId) {
                console.warn('No Antavo mission ID configured in CMS');
                return;
            }

            // Load all challenges from Antavo
            const challengesResponse = await this.getChallenges(this.customerModel.loyaltyId);

            if (challengesResponse?.payload) {
                // Find the specific mission by ID
                return challengesResponse.payload.find((challenge: NAntavo.IChallenge) => challenge.id === antavoMissionId);
            }
        } catch (error) {
            console.error('Failed to load first mission:', error);
            return undefined;
        }
        return;
    }

    get customerModel(): LocalCustomerModel {
        return this.customer.model as LocalCustomerModel;
    }

    /**
     * Gets purchase points preview from Antavo API
     * @param params Request parameters containing loyaltyId, transaction_id, total and optional items
     * @returns Promise with points preview data
     */
    async getPointsPreview(params: NAntavo.IPointsPreviewParams, status: Status): Promise<NAntavo.IPointsPreviewData | undefined> {
        try {
            return (await this.requestService.send(status, {
                path: `${this.BASE_PATH}/points-preview`,
                requestOptions: {
                    method: 'POST',
                    api: 'node'
                },
                data: {
                    loyaltyId: params.loyaltyId,
                    transaction_id: params.transaction_id,
                    total: params.total,
                    items: params.items
                }
            })) as NAntavo.IPointsPreviewData;
        } catch (error) {
            console.error('Error fetching points preview:', error);
            return undefined;
        }
    }

    /**
     * Parses a tag to extract limit and property name for dynamic progress bars
     * Expected format: "Post {limit} {property_name}" (case-insensitive)
     * @param tag The tag to parse
     * @returns Object with limit and property name, or undefined if tag doesn't match pattern
     */
    parsePostTag(tag: string): { limit: number; propertyName: string } | undefined {
        const match = tag.match(/^post\s+(\d+)\s+(.+)$/i);
        if (match) {
            return {
                limit: parseInt(match[1], 10),
                propertyName: match[2]
            };
        }
        return undefined;
    }

    /**
     * Extracts sort order from mission tags array
     * Expected format: "Sort:X" where X is a number (case-insensitive)
     * @param tags Array of tags to search through
     * @returns Sort order number, or 999999 if no sort tag found (to put unsorted items at the end)
     */
    getMissionSortOrder(tags: string[]): number {
        const sortTag = tags?.find(tag => tag.toLowerCase().startsWith('sort:'));
        if (sortTag) {
            const match = sortTag.match(/^sort:(\d+)$/i);
            if (match) {
                return parseInt(match[1], 10);
            }
        }

        return 999999;
    }

    /**
     * Gets the current value for a property from Antavo customer data
     * @param propertyName The property name to look up
     * @returns The current value or 0 if not found
     */
    getCustomerPropertyValue(propertyName: string): number {
        const antavoData = this.customerModel.antavoCustomerData.value;
        if (antavoData?.properties && propertyName in antavoData.properties) {
            return antavoData.properties[propertyName] || 0;
        }
        return 0;
    }

    /**
     * Gets dynamic progress data for the first tag that starts with "Post" or "post"
     * @param challenge The challenge to get progress data for
     * @returns Single progress data object or undefined if no matching tag found
     */
    getDynamicProgressData(
        challenge: NAntavo.IChallenge
    ): { limit: number; current: number; propertyName: string; displayName: string } | undefined {
        if (!challenge.tags) return undefined;

        // Find the first tag that starts with "Post" or "post" (case-insensitive)
        const postTag = challenge.tags.find(tag => tag.toLowerCase().startsWith('post'));

        if (!postTag) return undefined;

        const parsed = this.parsePostTag(postTag);
        if (!parsed) return undefined;

        return {
            limit: parsed.limit,
            current: this.getCustomerPropertyValue(parsed.propertyName),
            propertyName: parsed.propertyName,
            displayName:
                challenge.progress.criteria?.[0].message || parsed.propertyName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        };
    }

    /**
     * Determines if a mission is completed based on either:
     * 1. Original criteria (overall progress = 100%), OR
     * 2. Customer property value >= Post limit from tag
     * @param challenge The challenge to check
     * @returns True if mission is completed
     */
    isMissionCompleted(challenge: NAntavo.IChallenge): boolean {
        // Custom criteria for posts
        if (challenge.tags) {
            const postTag = challenge.tags.find(tag => tag.toLowerCase().startsWith('post'));
            if (postTag) {
                const parsed = this.parsePostTag(postTag);
                if (parsed) {
                    const currentValue = this.getCustomerPropertyValue(parsed.propertyName);
                    return currentValue >= parsed.limit;
                }
            }
        }

        return challenge.progress.overall === 100;
    }

    /**
     * Gets the progress percentage for display in progress indicators
     * Uses Post tag progress if available, otherwise falls back to overall progress
     * @param challenge The challenge to get progress for
     * @returns Progress percentage (0-100)
     */
    getMissionProgressPercentage(challenge: NAntavo.IChallenge): number {
        // Check if there's a Post tag for dynamic progress
        if (challenge.tags) {
            const postTag = challenge.tags.find(tag => tag.toLowerCase().startsWith('post'));
            if (postTag) {
                const parsed = this.parsePostTag(postTag);
                if (parsed) {
                    const currentValue = this.getCustomerPropertyValue(parsed.propertyName);
                    return Math.min((currentValue / parsed.limit) * 100, 100);
                }
            }
        }

        // Fall back to original overall progress
        return challenge.progress.overall;
    }

    /**
     * Clears the challenges cache to force fresh data on next request
     * Call this method after mission completion to ensure updated mission status
     */
    private clearChallengesCache(): void {
        this.challengesResponseCache = undefined;
    }

    /**
     * Refreshes customer member status data from Antavo
     * Call this method after mission completion to update tier information
     */
    private async refreshMemberStatus(): Promise<void> {
        if (this.customerModel) {
            await this.customerModel.refreshAntavoMemberStatus(this.customerModel.data);
        }
    }
}
